import { useEffectiveEntityRight } from "@/domain/_shared/entity-permission/hooks/api/use-effective-entity-right";
import { useFeatureFlagSimple } from "@/domain/_shared/feature-flag/hooks/api/use-feature-flag-simple";
import { OwnedItemTypes, OwnerTypes } from "@/domain/_shared/lib/owner-types";
import { useUnsafeCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ActionType, ResourceType } from "../../models/permission-types";
import { getEntityTypeForResource, hasRequiredRight } from "../../models/permission-v2-mapping";

import { checkPermissionQueryDocument } from "./permission.gql";

interface UseCheckPermissionProps {
  resource: ResourceType;
  action: ActionType;
  resourceId?: string;
  enabled?: boolean;
}

export const useCheckPermission = ({ resource, action, resourceId, enabled = true }: UseCheckPermissionProps) => {
  const queryClient = useQueryClient();
  const workspaceContext = useUnsafeCurrentWorkspaceContext();
  const { isEnabled: isV2Enabled } = useFeatureFlagSimple({ id: "enable-permission-v2" });
  const entityType = getEntityTypeForResource(resource);
  const workspaceId = workspaceContext?.currentWorkspace.id ?? "";
  const useV2 = isV2Enabled && entityType !== null && !!workspaceId;

  const { effectiveRight, loading: v2Loading, error: v2Error } = useEffectiveEntityRight({
    entityId: resourceId ?? "",
    entityType: entityType ?? "",
    workspaceId,
    enabled: enabled && !!resourceId && useV2,
  });

  const query = useQuery({
    queryKey: queryKeys.permissions.check(resource, action, resourceId || ""),
    queryFn: async () => {
      const client = createOperationClient("CheckPermission");
      const data = await client.request(checkPermissionQueryDocument, {
        input: {
          resource,
          action,
          resourceId: resourceId || "",
        },
      });
      return data.checkPermission;
    },
    enabled: enabled && !!resourceId && !useV2,
    staleTime: 5 * 60 * 1000,
  });

  const v1HasPermission = query.data?.hasPermission ?? false;

  if (useV2) {
    return {
      refetch: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.entityPermissions.all });
        return Promise.resolve();
      },
      loading: v2Loading,
      error: v2Error,
      hasPermission: hasRequiredRight(effectiveRight, action),
    };
  }

  return {
    refetch: query.refetch,
    loading: query.isLoading,
    error: query.error,
    hasPermission: v1HasPermission,
  };
};

export const getPermissionKeyFromOwnerType = (ownerType: string): ResourceType => {
  switch (ownerType) {
    case OwnerTypes.CLIMATE_ACTION:
      return "climateAction";
    case OwnerTypes.PROJECT:
      return "project";
    case OwnerTypes.TASK:
      return "task";
    case OwnerTypes.TEAM:
      return "team";

    default:
      return "workspace";
  }
};

export const getPermissionKeyFromOwnedItemType = (ownedItemType: string): ResourceType => {
  switch (ownedItemType) {
    case OwnedItemTypes.INDICATOR:
      return "indicator";
    default:
      return getPermissionKeyFromOwnerType(ownedItemType);
  }
};
